/**
 * Product detail gallery: prev/next arrows overlaid on the main image, swipe
 * gestures on that same image, a compact thumbnail grid, and a fullscreen
 * lightbox (opened by tapping the main image) all drive the same index and
 * stay in sync with each other. srcset is cleared on swap — otherwise the
 * browser would keep picking a source from the original responsive srcset
 * instead of respecting the new src we just set.
 *
 * The lightbox itself is the theme's native dialog-component (showModal /
 * closeDialog / Escape / outside-click already handled there) — this module
 * only supplies the gallery-specific behavior: which image it shows, the
 * click-to-zoom toggle, and swipe navigation on the main (non-fullscreen)
 * image. Swipe is intentionally only wired up on the main image, not inside
 * the lightbox, so it can't fight the lightbox's own zoom/pan interaction.
 */
function initProductGallery() {
  document.querySelectorAll('[data-product-gallery]').forEach((gallery) => {
    const mainImage = gallery.querySelector('.product-detail__main-image-img');
    const prevButton = gallery.querySelector('[data-gallery-prev]');
    const nextButton = gallery.querySelector('[data-gallery-next]');
    const thumbs = Array.from(gallery.querySelectorAll('[data-gallery-thumb]'));
    const zoomTrigger = gallery.querySelector('[data-gallery-zoom-trigger]');

    const thumbGrid = gallery.querySelector('[data-gallery-thumbs]');
    const card = gallery.querySelector('.product-detail__main-image-card');

    const dialogHost = gallery.querySelector('dialog-component');
    const lightboxImage = gallery.querySelector('[data-lightbox-image]');
    const lightboxPrev = gallery.querySelector('[data-lightbox-prev]');
    const lightboxNext = gallery.querySelector('[data-lightbox-next]');
    const lightboxThumbs = Array.from(gallery.querySelectorAll('[data-lightbox-thumb]'));

    if (!mainImage) return;

    let urls = [];
    try {
      urls = JSON.parse(gallery.getAttribute('data-media-urls') || '[]');
    } catch {
      urls = [];
    }

    let lightboxUrls = [];
    try {
      lightboxUrls = JSON.parse(gallery.getAttribute('data-lightbox-urls') || '[]');
    } catch {
      lightboxUrls = [];
    }

    let index = 0;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const SLIDE_OFFSET = 14;
    const SLIDE_DURATION = 220;
    const SLIDE_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';

    function updateAuxiliary(newIndex) {
      thumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === newIndex));
      lightboxThumbs.forEach((thumb, i) => thumb.classList.toggle('is-active', i === newIndex));

      if (lightboxImage && lightboxUrls[newIndex]) {
        lightboxImage.src = lightboxUrls[newIndex];
        lightboxImage.classList.remove('is-zoomed');
      }
    }

    function setMainImageSrc(newIndex) {
      mainImage.removeAttribute('srcset');
      mainImage.src = urls[newIndex];
    }

    /**
     * @param {number} nextIndex - target index (wraps, so out-of-range is fine)
     * @param {number} [direction] - -1 (previous), 1 (next), or 0/omitted for
     *   an undirected jump (e.g. a thumbnail click) — only directed calls get
     *   the slide animation, since "which way did we jump" isn't meaningful
     *   without one.
     */
    function show(nextIndex, direction = 0) {
      if (!urls.length) return;
      const newIndex = (nextIndex + urls.length) % urls.length;
      if (newIndex === index) return;

      if (!direction || reduceMotion) {
        index = newIndex;
        setMainImageSrc(newIndex);
        updateAuxiliary(newIndex);
        return;
      }

      // Slide the current photo out (toward the direction it's leaving), swap
      // the src once it's faded, then slide the new one in from the opposite
      // edge — a single <img> swapped mid-transition rather than two stacked
      // elements, which keeps this cheap and avoids layout/measurement work.
      mainImage.style.transition = `opacity ${SLIDE_DURATION}ms ${SLIDE_EASING}, transform ${SLIDE_DURATION}ms ${SLIDE_EASING}`;
      mainImage.style.opacity = '0';
      mainImage.style.transform = `translateX(${direction * -SLIDE_OFFSET}px)`;

      window.setTimeout(() => {
        index = newIndex;
        setMainImageSrc(newIndex);
        updateAuxiliary(newIndex);

        mainImage.style.transition = 'none';
        mainImage.style.transform = `translateX(${direction * SLIDE_OFFSET}px)`;
        void mainImage.offsetWidth; // force reflow so the jump above isn't itself animated
        mainImage.style.transition = `opacity ${SLIDE_DURATION}ms ${SLIDE_EASING}, transform ${SLIDE_DURATION}ms ${SLIDE_EASING}`;
        mainImage.style.opacity = '1';
        mainImage.style.transform = 'translateX(0)';
      }, SLIDE_DURATION);
    }

    prevButton?.addEventListener('click', () => show(index - 1, -1));
    nextButton?.addEventListener('click', () => show(index + 1, 1));
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        const target = Number(thumb.dataset.index);
        show(target, target > index ? 1 : target < index ? -1 : 0);
      });
    });

    // Swipe on the main image — main gallery only, not the lightbox (see
    // module doc comment). touch-action: pan-y (set in CSS) leaves vertical
    // page scroll to the browser while these listeners read horizontal
    // gesture distance themselves.
    if (zoomTrigger && urls.length > 1) {
      let touchStartX = 0;
      let touchStartY = 0;
      let touchMoved = false;
      let swipeHandled = false;
      const SWIPE_THRESHOLD = 40;
      const TAP_TOLERANCE = 10;

      zoomTrigger.addEventListener(
        'touchstart',
        (event) => {
          if (event.touches.length !== 1) return;
          touchStartX = event.touches[0].clientX;
          touchStartY = event.touches[0].clientY;
          touchMoved = false;
          swipeHandled = false;
        },
        { passive: true }
      );

      zoomTrigger.addEventListener(
        'touchmove',
        (event) => {
          if (event.touches.length !== 1) return;
          const dx = event.touches[0].clientX - touchStartX;
          const dy = event.touches[0].clientY - touchStartY;
          if (Math.abs(dx) > TAP_TOLERANCE || Math.abs(dy) > TAP_TOLERANCE) touchMoved = true;
        },
        { passive: true }
      );

      zoomTrigger.addEventListener('touchend', (event) => {
        if (!touchMoved) return;
        const touch = event.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - touchStartX;
        const dy = touch.clientY - touchStartY;
        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          swipeHandled = true;
          show(dx < 0 ? index + 1 : index - 1, dx < 0 ? 1 : -1);
        }
      });

      // A swipe still ends in a native 'click' on a <button> — suppress just
      // that one so it doesn't also pop the lightbox open right after
      // navigating. Plain taps (touchMoved stays false) are untouched.
      zoomTrigger.addEventListener('click', (event) => {
        if (!swipeHandled) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        swipeHandled = false;
      });
    }

    zoomTrigger?.addEventListener('click', () => {
      if (lightboxImage && !lightboxImage.getAttribute('src') && lightboxUrls[index]) {
        lightboxImage.src = lightboxUrls[index];
      }
      if (typeof dialogHost?.showDialog === 'function') {
        dialogHost.showDialog();
      }
    });

    lightboxPrev?.addEventListener('click', () => show(index - 1));
    lightboxNext?.addEventListener('click', () => show(index + 1));
    lightboxThumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => show(Number(thumb.dataset.index)));
    });

    lightboxImage?.addEventListener('click', (event) => {
      if (lightboxImage.classList.contains('is-zoomed')) {
        lightboxImage.classList.remove('is-zoomed');
        lightboxImage.style.transformOrigin = '';
        return;
      }
      const rect = lightboxImage.getBoundingClientRect();
      const originX = ((event.clientX - rect.left) / rect.width) * 100;
      const originY = ((event.clientY - rect.top) / rect.height) * 100;
      lightboxImage.style.transformOrigin = `${originX}% ${originY}%`;
      lightboxImage.classList.add('is-zoomed');
    });

    dialogHost?.addEventListener('dialog:close', () => {
      lightboxImage?.classList.remove('is-zoomed');
    });

    // Grow each thumbnail so the 2-column grid's total height (rows × size +
    // gaps) matches the main image card's rendered height exactly — the two
    // must sit flush, so this is solved from measured pixels rather than a
    // fixed CSS size. Only applies once thumbnails sit beside the image
    // (desktop); below 900px they stack under it at their default size.
    if (thumbGrid && card && thumbs.length) {
      const desktopQuery = window.matchMedia('(min-width: 901px)');
      const gap = 10;
      const columns = 2;
      const rows = Math.ceil(thumbs.length / columns);

      const syncThumbSize = () => {
        if (!desktopQuery.matches) {
          thumbGrid.style.removeProperty('--thumb-size');
          return;
        }
        const cardHeight = card.getBoundingClientRect().height;
        if (!cardHeight) return;
        const size = (cardHeight - (rows - 1) * gap) / rows;
        thumbGrid.style.setProperty('--thumb-size', `${Math.max(size, 0)}px`);
      };

      syncThumbSize();
      new ResizeObserver(syncThumbSize).observe(card);
      desktopQuery.addEventListener('change', syncThumbSize);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductGallery);
} else {
  initProductGallery();
}
